import type { Dayjs } from 'dayjs';
import type {
  MaterialAdmin,
  MaterialInput as EntradaDoMaterial,
  MaterialLinkDestination as DestinoDoLink,
} from '@titularis/api-client';

/**
 * A mesa dos materiais grátis, sem React: o que a tela converte e decide.
 *
 * O servidor confere tudo de novo (destino interno, forma da chave, slug
 * travado), então o que mora aqui é conforto de quem publica - dizer o erro
 * antes de salvar - e nunca a regra que protege o link distribuído.
 */

export type SituacaoDoMaterial = 'rascunho' | 'agendado' | 'no_ar';

export const SITUACAO_LABEL: Record<SituacaoDoMaterial, string> = {
  rascunho: 'Rascunho',
  agendado: 'Agendado',
  no_ar: 'No ar',
};

export const SITUACAO_COR: Record<SituacaoDoMaterial, string> = {
  rascunho: 'default',
  agendado: 'processing',
  no_ar: 'success',
};

/** A data é a publicação, como nas Notícias e na Revista: não há botão "publicar". */
export function situacaoDoMaterial(
  publishedAt: string | null,
  agora: Date = new Date(),
): SituacaoDoMaterial {
  if (!publishedAt) return 'rascunho';
  return new Date(publishedAt) > agora ? 'agendado' : 'no_ar';
}

/** Uma linha da tabela de links, como o formulário a guarda. */
export interface LinhaDeLink {
  chave: string;
  onde: DestinoDoLink['onde'];
  caminho: string;
}

export interface ValoresDoMaterial {
  title: string;
  slug?: string;
  summary: string;
  description?: string;
  fileName?: string | null;
  publishedAt?: Dayjs | null;
  padraoOnde?: DestinoDoLink['onde'];
  padraoCaminho?: string;
  chaves?: LinhaDeLink[];
}

/** O formato de slug e de chave, o mesmo da API e do PDF. */
export const FORMATO_DE_ENDERECO = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** O tamanho máximo de uma chave, o mesmo que a API aceita. */
export const TAMANHO_DA_CHAVE = 60;

/**
 * O tamanho máximo de um destino, o mesmo que a API aceita.
 *
 * Cabe o maior link que o Estudar abre: 100 questões pelo id. O dia de
 * simulado do cronograma leva as 83 de uma prova inteira.
 */
export const TAMANHO_DO_DESTINO = 4000;

/** "O limite é 4.000", para as mensagens de quem cola um destino longo demais. */
export const limiteDoDestino = (caminho: string): string =>
  `tem ${caminho.length.toLocaleString('pt-BR')} caracteres, e o limite é ${TAMANHO_DO_DESTINO.toLocaleString('pt-BR')}`;

/**
 * As chaves que aparecem mais de uma vez na tabela.
 *
 * Duas linhas com a mesma chave viram uma só no objeto que vai para a API, e a
 * primeira some calada - o link impresso passaria a levar para o destino da
 * segunda sem ninguém ter escolhido isso.
 */
export function chavesRepetidas(linhas: (LinhaDeLink | undefined)[] | undefined): string[] {
  const vistas = new Set<string>();
  const repetidas = new Set<string>();
  for (const linha of linhas ?? []) {
    const chave = linha?.chave?.trim();
    if (!chave) continue;
    if (vistas.has(chave)) repetidas.add(chave);
    vistas.add(chave);
  }
  return [...repetidas];
}

/** Caminho interno: começa com uma barra só. O servidor confere de novo. */
export const caminhoValido = (caminho: string): boolean =>
  caminho.startsWith('/') && !caminho.startsWith('//') && !caminho.startsWith('/\\');

export function valoresDoMaterial(material: MaterialAdmin | null): Partial<ValoresDoMaterial> {
  if (!material) return { padraoOnde: 'app', chaves: [] };
  return {
    title: material.title,
    slug: material.slug,
    summary: material.summary,
    description: material.description,
    fileName: material.fileName,
    padraoOnde: material.links.padrao?.onde ?? 'app',
    padraoCaminho: material.links.padrao?.caminho,
    chaves: Object.entries(material.links.chaves).map(([chave, destino]) => ({
      chave,
      onde: destino.onde,
      caminho: destino.caminho,
    })),
  };
}

/**
 * O que vai para a API.
 *
 * **Tudo vai, sempre**: a gaveta mostra o cadastro inteiro, então uma data
 * apagada no campo é uma despublicação pedida de propósito. O `PATCH` da API só
 * deixa de mexer no que não veio, e daqui tudo vem.
 */
export function entradaDoMaterial(valores: ValoresDoMaterial): EntradaDoMaterial {
  const caminhoPadrao = valores.padraoCaminho?.trim();

  return {
    title: valores.title.trim(),
    ...(valores.slug?.trim() ? { slug: valores.slug.trim() } : {}),
    summary: valores.summary.trim(),
    description: valores.description?.trim() ?? '',
    fileName: valores.fileName?.trim() || null,
    publishedAt: valores.publishedAt ? valores.publishedAt.toISOString() : null,
    links: {
      ...(caminhoPadrao ? { padrao: { onde: valores.padraoOnde ?? 'app', caminho: caminhoPadrao } } : {}),
      chaves: Object.fromEntries(
        (valores.chaves ?? [])
          .filter((linha) => linha?.chave?.trim())
          .map((linha) => [linha.chave.trim(), { onde: linha.onde, caminho: linha.caminho.trim() }]),
      ),
    },
  };
}

/**
 * Lê o JSON de links colado na gaveta.
 *
 * Existe para o cronograma: 54 dias, e uma chave por questão, não se digitam à
 * mão. O formato é o mesmo que a API guarda - `{ "chave": { "onde": "app",
 * "caminho": "/estudar?..." } }` -, e aceita também o objeto inteiro de links,
 * com `padrao` e `chaves`, como o script das questões do dia escreve.
 */
export function linhasDoJson(texto: string): { linhas: LinhaDeLink[]; padrao?: DestinoDoLink } {
  let bruto: unknown;
  try {
    bruto = JSON.parse(texto);
  } catch {
    throw new Error('O texto não é um JSON válido.');
  }
  if (!bruto || typeof bruto !== 'object' || Array.isArray(bruto)) {
    throw new Error('O JSON precisa ser um objeto de chaves.');
  }

  const objeto = bruto as Record<string, unknown>;
  const chaves = (objeto.chaves && typeof objeto.chaves === 'object' ? objeto.chaves : objeto) as Record<
    string,
    unknown
  >;
  // `padrao` vale nas duas formas: ao lado de `chaves` ou solto entre as chaves.
  const padrao = destinoDe('padrao', objeto.padrao, true);

  const linhas = Object.entries(chaves)
    .filter(([chave]) => !(objeto.chaves === undefined && chave === 'padrao'))
    .map(([chave, destino]) => {
      if (!FORMATO_DE_ENDERECO.test(chave) || chave.length > TAMANHO_DA_CHAVE) {
        throw new Error(
          `A chave "${chave}" precisa ter até ${TAMANHO_DA_CHAVE} caracteres: letras minúsculas sem acento, números e hífen.`,
        );
      }
      return { chave, ...destinoDe(chave, destino, false)! };
    });

  return { linhas, ...(padrao ? { padrao } : {}) };
}

function destinoDe(chave: string, valor: unknown, opcional: boolean): DestinoDoLink | undefined {
  if (valor === undefined && opcional) return undefined;
  const destino = valor as Partial<DestinoDoLink> | null;
  if (
    !destino ||
    (destino.onde !== 'app' && destino.onde !== 'site') ||
    typeof destino.caminho !== 'string' ||
    !caminhoValido(destino.caminho)
  ) {
    throw new Error(`O destino de "${chave}" precisa de "onde" (app ou site) e de um caminho interno.`);
  }
  if (destino.caminho.length > TAMANHO_DO_DESTINO) {
    throw new Error(`O destino de "${chave}" ${limiteDoDestino(destino.caminho)}.`);
  }
  return { onde: destino.onde, caminho: destino.caminho };
}

/**
 * As chaves que o PDF tem e a gaveta não liga a destino nenhum.
 *
 * Sem destino padrão, elas levam à página do material - não quebram, mas
 * provavelmente não era isso que o autor quis (G-AMBIGUITY). Com padrão, vão
 * para ele, e a lista serve só de conferência.
 */
export function chavesSemDestino(material: MaterialAdmin): string[] {
  const ligadas = new Set(Object.keys(material.links.chaves));
  return (material.pdfInventory?.chaves ?? [])
    .filter((item) => item.slug === material.slug && !ligadas.has(item.chave))
    .map((item) => item.chave);
}

/**
 * As chaves do PDF que apontam para **outro** material.
 *
 * Um PDF copiado de outro material e reenviado aqui traz os links do de
 * origem: eles funcionam, mas contam lá, e não aqui.
 */
export function chavesDeOutroMaterial(material: MaterialAdmin): string[] {
  return (material.pdfInventory?.chaves ?? [])
    .filter((item) => item.slug !== material.slug)
    .map((item) => `${item.slug}/${item.chave}`);
}
